import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Slide,
  Link,
  IconButton,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

// ============================================================================
// COOKIE CONSENT BANNER
// Simple consent banner for essential cookies only (auth sessions)
// ============================================================================

const CONSENT_KEY = 'cookie_consent';
const CONSENT_DATE_KEY = 'cookie_consent_date';
const CONSENT_EXPIRY_MONTHS = 12;

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);

  // Check for existing consent on mount
  useEffect(() => {
    const checkConsent = () => {
      const consent = localStorage.getItem(CONSENT_KEY);
      const consentDate = localStorage.getItem(CONSENT_DATE_KEY);

      if (!consent || !consentDate) {
        // No consent recorded - show banner
        setShowBanner(true);
        return;
      }

      // Check if consent has expired (12 months)
      const consentTimestamp = new Date(consentDate).getTime();
      const now = new Date().getTime();
      const monthsElapsed = (now - consentTimestamp) / (1000 * 60 * 60 * 24 * 30);

      if (monthsElapsed > CONSENT_EXPIRY_MONTHS) {
        // Consent expired - show banner again
        setShowBanner(true);
      }
    };

    // Small delay to prevent flash on page load
    const timer = setTimeout(checkConsent, 500);
    return () => clearTimeout(timer);
  }, []);

  // Save consent acknowledgment to localStorage
  const acknowledgeConsent = () => {
    localStorage.setItem(CONSENT_KEY, 'acknowledged');
    localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
    setShowBanner(false);
    console.log('🍪 Cookie consent acknowledged');
  };

  if (!showBanner) return null;

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <CookieIcon sx={{ fontSize: 32, color: 'primary.main', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                We use essential cookies
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This site uses essential cookies for authentication and security. 
                These are required for the platform to function and cannot be disabled.
                We do not use tracking or marketing cookies.{' '}
                <Link href="/cookie-policy" target="_blank">
                  Learn more
                </Link>
              </Typography>

              {/* Action Button */}
              <Button
                variant="contained"
                color="primary"
                onClick={acknowledgeConsent}
                startIcon={<CheckIcon />}
                sx={{ minWidth: 140 }}
                data-testid="cookie-consent-accept"
              >
                Got It
              </Button>
            </Box>
            <IconButton
              size="small"
              onClick={acknowledgeConsent}
              sx={{ color: 'text.secondary' }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

// ============================================================================
// COOKIE SETTINGS BUTTON
// Button to re-open cookie notice (for footer)
// ============================================================================

export const CookieSettingsButton: React.FC = () => {
  const openCookieNotice = () => {
    // Clear consent to trigger banner
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(CONSENT_DATE_KEY);
    // Force page reload to show banner
    window.location.reload();
  };

  return (
    <Button
      variant="text"
      size="small"
      onClick={openCookieNotice}
      startIcon={<CookieIcon />}
      sx={{ color: 'text.secondary' }}
    >
      Cookie Notice
    </Button>
  );
};

export default CookieConsent;
